import { AxiosError } from 'axios'
import { datadogLogs, type LogsEvent, type LogsEventDomainContext } from '@datadog/browser-logs'
import { datadogRum, type RumEvent, type RumEventDomainContext } from '@datadog/browser-rum'

import { getCurrentNativeAppVersionFromUserAgent } from '@dhl/native-bridge'
import { isNative } from '@dhl/webview'
import { getDataLayer } from '../analytics/init-datalayer.js'

type IgnoreLogsEvent = (event: LogsEvent, context: LogsEventDomainContext) => boolean
type IgnoreRumEvent = (event: RumEvent, context: RumEventDomainContext) => boolean

export type DatadogConfig = {
  applicationId: string
  clientToken: string
  environment: string
  ignoreLogsErrors?: IgnoreLogsEvent[]
  ignoreRumErrors?: IgnoreRumEvent[]
  logsSessionSampleRate: number
  rumSessionReplaySampleRate: number
  rumSessionSampleRate: number
  service: string
  userId?: string
  version: string
}

type InternalDatadogConfig = {
  trackingConsent: 'granted' | 'not-granted'
}

const site = 'datadoghq.eu'

export const setDatadogTrackingConsent = (consent: 'granted' | 'not-granted'): void => {
  datadogLogs.setTrackingConsent(consent)
  datadogRum.setTrackingConsent(consent)
}

const excludedUrls = [
  'assets.adobedtm.com',
  'cdn.cookielaw.org',
  'mopinion.com',
  'onetrust.com',
  'demdex.net',
  'static.cloudflareinsights.com',
]

const defaultIgnoreLogsErrors: IgnoreLogsEvent[] = [
  (event) => excludedUrls.some((url) => !!event.http?.url?.includes(url)),
  (event) =>
    (!!event.http?.status_code && event.http.status_code < 500) ||
    (!!event?.error && 'status' in event.error && (event.error.status as number) < 500),
  (event) => excludedUrls.some((url) => !!event.error?.stack?.includes(url)),
  (event) => !!event?.error?.stack?.includes('Error: Script error'),
  (event) => event.http?.status_code === 0,
  (_, context) => !!context && 'isAborted' in context && context.isAborted,
  (event) =>
    [event?.error?.message, event?.error?.stack, event?.error?.message].some(
      (msg) => !!msg?.includes("Can't write cookie on domain *.dhlexpress.nl")
    ),
  (event) =>
    [event?.error?.message, event?.error?.stack, event?.error?.message].some(
      (msg) => !!msg?.includes('_pk_testcookie_domain')
    ),
]

const defaultIgnoreRumErrors: IgnoreRumEvent[] = [
  (event) => event.type === 'error' && !!event.error.csp && event.error.source === 'report',
  (event) => event.type === 'error' && excludedUrls.some((url) => (event.error.handling_stack ?? '').includes(url)),
  (event) =>
    event.type === 'error' &&
    event.error.type === 'AxiosError' &&
    !!event.context?.status &&
    (event.context?.status as number) < 500,
  (event) => event.type === 'error' && event.context?.status === 0,
  (event) => event.type === 'error' && excludedUrls.some((url) => !!event.error?.stack?.includes(url)),
  (event) => event.type === 'error' && !!event?.error?.stack?.includes('Error: Script error'),
  (_, context) => 'isAborted' in context && context.isAborted,
  (event) =>
    event.type === 'error' &&
    [event?.error?.stack, event?.error?.message].some(
      (msg) => !!msg?.includes("Can't write cookie on domain *.dhlexpress.nl")
    ),
  (event) =>
    event.type === 'error' &&
    [event?.error?.stack, event?.error?.message].some((msg) => !!msg?.includes('_pk_testcookie_domain')),
]

export const initDatadog = ({
  applicationId,
  clientToken,
  environment,
  ignoreLogsErrors = [],
  ignoreRumErrors = [],
  logsSessionSampleRate,
  rumSessionReplaySampleRate,
  rumSessionSampleRate,
  service,
  trackingConsent,
  userId,
  version,
}: DatadogConfig & InternalDatadogConfig): void => {
  if (!clientToken) {
    return
  }

  let env = environment

  if (environment === 'production') {
    env = 'prod'
  }

  datadogLogs.init({
    env,
    clientToken,
    site,
    forwardErrorsToLogs: true,
    sessionSampleRate: logsSessionSampleRate,
    trackingConsent: 'granted',
    // this is the default, but forwardErrorsToLogs will make error logs as default to be sent anyway
    forwardConsoleLogs: [],
    // disable telemetry data about SDK execution
    telemetryConfigurationSampleRate: 0,
    telemetrySampleRate: 0,
    telemetryUsageSampleRate: 0,
    service,
    version,
    beforeSend(event, context) {
      const ignoredErrors = [...defaultIgnoreLogsErrors, ...ignoreLogsErrors]

      if (ignoredErrors.some((fn) => fn(event, context))) {
        return false
      }

      // @ts-expect-error
      if (event.error && event.error.config && event.error.config.data) {
        // Data may contain sensitive information, so we remove it from the logs
        // @ts-expect-error
        // eslint-disable-next-line no-param-reassign
        delete event.error.config.data
      }

      return true
    },
  })

  const additionalContext = {
    isNative: isNative(),
    version: isNative() ? getCurrentNativeAppVersionFromUserAgent() : undefined,
  }

  datadogLogs.setGlobalContextProperty('app', additionalContext)
  datadogRum.setGlobalContextProperty('app', additionalContext)

  if (userId) {
    datadogLogs.setUser({ id: userId })
    datadogRum.setUser({ id: userId })
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: rumSessionSampleRate,
    sessionReplaySampleRate: rumSessionReplaySampleRate,
    // disable telemetry data about SDK execution
    telemetryConfigurationSampleRate: 0,
    telemetrySampleRate: 0,
    telemetryUsageSampleRate: 0,
    trackingConsent,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackSessionAcrossSubdomains: true,
    defaultPrivacyLevel: 'mask',
    beforeSend(event, context) {
      if (event.view.url.includes('c2c')) {
        // For example, /portal/c2c becomes /portal/?.
        // eslint-disable-next-line no-param-reassign -- it is possible by docs
        event.view.url = event.view.url.replace('c2c', 'customer-to-customer')
      }

      const ignoredErrors = [...defaultIgnoreRumErrors, ...ignoreRumErrors]

      if (ignoredErrors.some((fn) => fn(event, context))) {
        return false
      }

      return true
    },
  })

  // listen to the generic middleware for analytics
  getDataLayer()?.subscribe((e) => {
    const { meta, event } = e
    const id = meta?.id || 'unknown'

    const trackData = {
      ...meta,
    }

    if (event === 'CLICK') {
      datadogRum.addAction(id, trackData)
      datadogLogs.logger.info(id, trackData)
    }

    if (event === 'EVENT') {
      datadogRum.addAction(id, trackData)
      datadogLogs.logger.info(id, trackData)
    }

    if (event === 'ERROR') {
      datadogLogs.logger.error(id, trackData)

      const { error, ...rest } = trackData

      if (error) {
        if (error instanceof AxiosError) {
          datadogRum.addError(error, {
            status: error.response?.status,
            statusText: error.response?.statusText,
            ...rest,
          })
        } else {
          datadogRum.addError(error, rest)
        }
      }
    }
  })
}
